import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useSite, scrollToId, EASE } from "../lib/site";

const LINKS = [["services", "services"], ["approach", "approach"], ["why", "why-us"], ["industries", "industries"], ["contact", "contact"]];

const LangToggle = ({ testId }) => {
  const { lang, setLang } = useSite();
  return (
    <button
      data-testid={testId}
      onClick={() => setLang(lang === "en" ? "ar" : "en")}
      className="relative flex items-center rounded-full border border-[var(--line-gold)] p-1 text-xs font-['JetBrains_Mono']"
      dir="ltr"
      aria-label="Switch language"
    >
      {["en", "ar"].map((l) => (
        <span key={l} className={`relative z-10 px-3 py-1 rounded-full transition-colors duration-300 ${lang === l ? "text-[var(--ink)]" : "text-[var(--muted)]"}`}>
          {lang === l && <motion.span layoutId={`${testId}-pill`} className="absolute inset-0 -z-10 rounded-full bg-[var(--gold)]" transition={{ duration: 0.4, ease: EASE }} />}
          {l === "en" ? "EN" : <span className="font-['Cairo'] text-sm leading-none">ع</span>}
        </span>
      ))}
    </button>
  );
};

export const Nav = () => {
  const { t } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 0.9 }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled ? "bg-[#0B0E14]/80 backdrop-blur-xl border-[var(--line)]" : "bg-transparent border-transparent"}`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-6">
          <button onClick={() => go("top")} data-testid="nav-logo">
            <Logo />
          </button>
          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map(([k, id]) => (
              <button key={k} onClick={() => go(id)} data-testid={`nav-link-${id}`} className="group relative text-sm text-[var(--muted)] hover:text-[var(--paper)] transition-colors">
                {t.nav[k]}
                <span className="absolute -bottom-1 start-0 h-px w-full origin-left scale-x-0 bg-[var(--gold)] transition-transform duration-500 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle testId="lang-toggle-button" />
            <button onClick={() => go("contact")} data-testid="nav-cta" className="hidden md:inline-flex rounded-full bg-[var(--paper)] text-[var(--ink)] px-5 py-2.5 text-sm font-medium hover:bg-[var(--gold)] transition-colors duration-300">
              {t.nav.cta}
            </button>
            <button onClick={() => setOpen(true)} className="lg:hidden p-2 text-[var(--paper)]" data-testid="mobile-menu-open" aria-label={t.nav.menu}>
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] bg-[var(--ink)] flex flex-col px-6 py-6"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button onClick={() => setOpen(false)} className="p-2" data-testid="mobile-menu-close" aria-label={t.nav.close}><X className="w-7 h-7" /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-5">
              {LINKS.map(([k, id], i) => (
                <motion.button
                  key={k}
                  onClick={() => go(id)}
                  data-testid={`mobile-nav-link-${id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.07, duration: 0.6, ease: EASE }}
                  className="f-display text-4xl text-start text-[var(--paper)]"
                >
                  <span className="f-mono text-xs text-[var(--gold)] me-4 align-middle">0{i + 1}</span>{t.nav[k]}
                </motion.button>
              ))}
            </div>
            <LangToggle testId="mobile-lang-toggle" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
